import React from 'react';
import styled from 'styled-components';
import { Box, CircularProgress } from '@mui/material';
// @ts-expect-error - Vite SVG import typing issue
import { ReactComponent as MosaicIcon } from '../assets/mosaic_icon.svg';

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 300px;
  padding: 48px 32px;
  background: transparent;
`;

const LogoContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
`;

const SpinningLogoWrapper = styled.div`
  width: 32px;
  height: 32px;
  animation: spin 2s ease-in-out infinite;
  filter: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? 'brightness(1.2) drop-shadow(0 0 8px rgba(59, 130, 246, 0.3))'
      : 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.2))'};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const CircularProgressStyled = styled(CircularProgress)`
  position: absolute;
  color: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.6)' : 'rgba(37, 99, 235, 0.6)'};
  opacity: 0.8;
`;

const LoadingText = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(203, 213, 225, 0.6)' : 'rgba(71, 85, 105, 0.6)'};
  letter-spacing: 0.3px;
`;

interface TableLoadingStateProps {
  message?: string;
}

/**
 * TableLoadingState Component
 *
 * Elegant loading state for tables with small spinning Mosaic logo.
 * Prevents layout shift and provides visual feedback during data loading.
 *
 * Features:
 * - Small, non-intrusive design (32px logo)
 * - Smooth spinning animation (2s ease-in-out)
 * - Circular progress ring for additional feedback
 * - Theme-aware with subtle glow effects
 * - 300px min-height prevents layout shift
 *
 * @example
 * <TableLoadingState message="Loading data..." />
 */
const TableLoadingState: React.FC<TableLoadingStateProps> = ({ message = 'Loading data...' }) => {
  return (
    <LoadingContainer>
      <LogoContainer>
        <SpinningLogoWrapper>
          <MosaicIcon />
        </SpinningLogoWrapper>
        <CircularProgressStyled size={56} thickness={2} />
      </LogoContainer>
      <LoadingText>{message}</LoadingText>
    </LoadingContainer>
  );
};

export default TableLoadingState;
